export const useAuth = () => {
  const { $toast } = useNuxtApp()
  const config = useRuntimeConfig()

  const authTokenOnCookie = useCookie<string | undefined>(cookies.AUTH_TOKEN, {
    sameSite: 'lax',
    path: '/',
    maxAge: 60 * 60 * 24 * 14, // 14日
    default: undefined,
    secure: config.public.useSecureCookie,
    watch: true,
  })

  const authToken = useState<string>(
    'authToken',
    () => authTokenOnCookie.value || '',
  )

  const accountInfo = useState<GetAccount200Response | undefined>(
    'accountInfo',
    () => undefined,
  )
  const accountRoleInfo = useState<GetAccountRoles200Response | undefined>(
    'accountRoleInfo',
    () => undefined,
  )

  // 認証トークンの設定
  const setAuthToken = (newAuthToken: string) => {
    authToken.value = newAuthToken || ''
    authTokenOnCookie.value = newAuthToken
  }

  // 認証トークンの削除
  const clearAuthToken = () => {
    authToken.value = ''
    authTokenOnCookie.value = ''
  }

  // サインイン
  const signIn = async ({
    email,
    password,
    afterSignInTo = AFTER_SIGN_IN_PATH,
  }: {
    email: string
    password: string
    afterSignInTo?: string
  }) => {
    try {
      const { adminToken: at } = await useApi(CertificationApi).login({
        loginRequest: { email, password },
      })
      setAuthToken(at)
      navigateTo(afterSignInTo)
    } catch (e: any) {
      switch (e.response?.errorData?.status) {
        case apiErrorStatuses.INVALID_EMAIL_OR_PASSWORD:
          $toast.error('メールアドレス・パスワードをご確認ください。')
          break
        case apiErrorStatuses.ACCOUNT_LOCKED:
          $toast.error(
            '一定回数ログインに失敗したため、アカウントがロックされています。\nReDesigner運営までお問い合わせください。',
          )
          break
        default:
          $toast.apiError('ログイン時にエラーが発生しました', e)
          break
      }
    }
  }

  // ユーザーステータス取得
  const refreshAccountInfo = async (force = false) => {
    // 認証トークンがない場合は取得しない
    if (!authToken.value) {
      accountInfo.value = undefined
      accountRoleInfo.value = undefined
      return
    }
    // すでにユーザー情報がある場合は取得しない
    if (!force && accountRoleInfo.value) {
      return
    }

    try {
      const accountResult = await useApi(AccountApi).getAccount()
      const roleResult = await useApi(PermissionApi).getAccountRoles()

      if (accountResult) {
        accountInfo.value = accountResult
      }
      if (roleResult) {
        accountRoleInfo.value = roleResult
      }
    } catch {
      accountInfo.value = undefined
      accountRoleInfo.value = undefined
      console.error('ユーザーステータス取得に失敗しました')
      $toast.error('ユーザーステータス取得に失敗しました')
    }
  }

  // 期限切れサインアウト
  const signOutOnExpired = () => {
    clearAuthToken()
    /**
     * クライアントタイミングではリロードすることでページ毎の認証の必要有無チェックを再度走らせる
     */
    if (import.meta.client) {
      window.location.href = `/login?to=${encodeURIComponent(
        useRoute().fullPath,
      )}`
    }
  }

  // サインアウト
  const signOut = async () => {
    await useApi(CertificationApi).logout()
    clearAuthToken()
    if (import.meta.client) {
      window.location.href = '/login'
    }
  }

  const setSubstituteMode = async (companyCode: string) => {
    const result = await useApi(AccountApi).getTokenSubstitute({
      getTokenSubstituteRequest: { companyCode },
    })
    authTokenOnCookie.value = result.adminToken
    window.location.href = '/'
  }
  const clearSubstituteMode = async () => {
    const result = await useApi(AccountApi).getResetTokenSubstitute()
    authTokenOnCookie.value = result.adminToken
    window.location.href = '/'
  }

  return {
    refreshAccountInfo,
    accountInfo,
    accountRoleInfo,
    isAdmin: computed(() => accountInfo.value?.companyType === 1),
    isAssistMode: computed(() => {
      return Boolean(accountInfo.value?.substituteStatus)
    }),
    // 意欲機能の表示可否（会社レベル機能フラグ、未取得時は非表示）
    intentionVisible: computed(
      () => accountInfo.value?.featureFlags?.intention?.visible ?? false,
    ),
    // AI課題機能の表示可否（課題が1件以上ある企業でtrue、未取得時は非表示）
    designChallengeWithAiVisible: computed(
      () =>
        accountInfo.value?.featureFlags?.designChallengeWithAi202608 ?? false,
    ),
    afterRegisterCompanyInfo: computed(() => {
      if (!accountInfo.value) {
        return false
      }
      const { companyLogoImagePath, publicFlg } = accountInfo.value
      return (
        Boolean(companyLogoImagePath) && publicFlg === companyPublicFlg.PUBLISH
      )
    }),
    authToken: readonly(authToken),
    signedIn: computed(() => Boolean(accountRoleInfo.value)),
    signIn,
    signOut,
    signOutOnExpired,
    setAuthToken,
    clearAuthToken,
    setSubstituteMode,
    clearSubstituteMode,
  }
}
